import React from 'react';
import {
  ViewColumnsIcon,
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

// Add CSS for custom scrollbar
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .hide-scrollbar::-webkit-scrollbar {
    height: 4px;
    width: 4px;
  }

  .hide-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

export default function KanbanHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      {/* Add custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ViewColumnsIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Kanban Board</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Visual task management system using Kanban methodology to organize project tasks
            into columns representing different stages of completion and progress tracking.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ViewColumnsIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Kanban Board Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <ClockIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">To Do</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Tasks that have been planned but not yet started. These represent
                  upcoming work that needs to be initiated.
                </p>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 border border-yellow-200">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <PlayIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">In Progress</h3>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Tasks currently being worked on. Typically limited to prevent
                  work overload and maintain focus.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Completed</h3>
                <p className="text-green-700 text-sm leading-relaxed">
                  Tasks that have been finished and meet quality standards.
                  These represent completed work items.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Task Details</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Each task card contains detailed information including description,
                  assigned staff, due dates, and role requirements.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Kanban Board Layout</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Kanban Board Structure */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Project Tasks Overview</h3>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Tasks</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* To Do Column */}
                  <div className="border rounded-lg bg-gray-50 p-4">
                    <h4 className="font-medium text-gray-900 flex items-center mb-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-blue-400 mr-2"></span>
                      To Do
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-blue-400">
                        <p className="font-medium text-sm">Staff Schedule Review</p>
                        <p className="text-xs text-gray-500 mt-1">Review and approve weekly staff schedules</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">15 Oct</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            Manager
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-blue-400">
                        <p className="font-medium text-sm">Inventory Count</p>
                        <p className="text-xs text-gray-500 mt-1">Conduct monthly inventory count</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">16 Oct</span>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            Staff
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* In Progress Column */}
                  <div className="border rounded-lg bg-yellow-50 p-4">
                    <h4 className="font-medium text-gray-900 flex items-center mb-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-yellow-500 mr-2"></span>
                      In Progress
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-yellow-500">
                        <p className="font-medium text-sm">Customer Service Training</p>
                        <p className="text-xs text-gray-500 mt-1">Complete customer service training module</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">14 Oct</span>
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            New Staff
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-yellow-500">
                        <p className="font-medium text-sm">Performance Review</p>
                        <p className="text-xs text-gray-500 mt-1">Conduct quarterly performance reviews</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">14 Oct</span>
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            Manager
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Completed Column */}
                  <div className="border rounded-lg bg-green-50 p-4">
                    <h4 className="font-medium text-gray-900 flex items-center mb-3">
                      <span className="h-2.5 w-2.5 rounded-full bg-green-500 mr-2"></span>
                      Completed
                    </h4>
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-green-500">
                        <p className="font-medium text-sm">Safety Inspection</p>
                        <p className="text-xs text-gray-500 mt-1">Complete monthly safety inspection</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">13 Oct</span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            Supervisor
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded shadow-sm border-l-4 border-green-500">
                        <p className="font-medium text-sm">Cash Register Audit</p>
                        <p className="text-xs text-gray-500 mt-1">Audit cash registers for accuracy</p>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-xs text-gray-600">12 Oct</span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            Cashier
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Task Card Details */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Task Card Structure</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Task Card Example */}
                <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                  <div className="border-l-4 border-blue-400 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 text-sm">Customer Order Processing</h4>
                        <p className="text-xs text-gray-600 mt-1">
                          Process incoming customer orders and update inventory system
                        </p>

                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-xs text-gray-600">Sarah Johnson</span>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            Staff
                          </span>
                        </div>

                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-xs text-gray-600">Due: 16 Oct 2025</span>
                          </div>
                          <span className="text-xs text-gray-500">High Priority</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Task Card Breakdown */}
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 text-sm mb-2">Task Title</h4>
                    <p className="text-blue-700 text-xs">Clear, actionable task name</p>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h4 className="font-semibold text-indigo-900 text-sm mb-2">Description</h4>
                    <p className="text-indigo-700 text-xs">Detailed explanation of task requirements</p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h4 className="font-semibold text-purple-900 text-sm mb-2">Assignee & Role</h4>
                    <p className="text-purple-700 text-xs">Staff member name and their role in the task</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <h4 className="font-semibold text-green-900 text-sm mb-2">Due Date</h4>
                    <p className="text-green-700 text-xs">Deadline for task completion</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Column Status Indicators */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Column Status Indicators</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center mb-3">
                    <span className="h-3 w-3 rounded-full bg-blue-400 mr-2"></span>
                    <h4 className="font-semibold text-gray-900">To Do</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Tasks awaiting initiation. Blue indicator shows planned work.
                  </p>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <div className="flex items-center mb-3">
                    <span className="h-3 w-3 rounded-full bg-yellow-500 mr-2"></span>
                    <h4 className="font-semibold text-gray-900">In Progress</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Active work in progress. Yellow indicator shows current focus.
                  </p>
                </div>

                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center mb-3">
                    <span className="h-3 w-3 rounded-full bg-green-500 mr-2"></span>
                    <h4 className="font-semibold text-gray-900">Completed</h4>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Finished tasks. Green indicator shows completed work.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Visual Organization</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Tasks are organized into three clear columns representing different
                  stages of work, making project status immediately visible.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Work-in-Progress Limits</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Limit tasks in "In Progress" to prevent work overload and maintain
                  focus on completing current tasks before starting new ones.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Task Details</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Each task card contains essential information including assignee,
                  due date, priority level, and detailed description.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Progress Tracking</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Move tasks between columns as work progresses, providing clear
                  visual indication of project advancement and completion status.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Role Assignment</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Tasks are clearly labeled with staff roles, making it easy to
                  identify who should work on specific tasks and track responsibilities.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Priority Management</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Tasks can be prioritized and organized within columns to ensure
                  critical work is addressed first and deadlines are met.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Kanban Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Workflow Management</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Move tasks promptly between columns as work progresses</li>
                    <li>• Don't start new tasks until current ones are completed</li>
                    <li>• Use "In Progress" limits to prevent work overload</li>
                    <li>• Regularly review and update task statuses</li>
                    <li>• Remove completed tasks to maintain board clarity</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Task Organization</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Keep task descriptions clear and actionable</li>
                    <li>• Assign tasks to specific team members</li>
                    <li>• Set realistic due dates for all tasks</li>
                    <li>• Use priority indicators for urgent work</li>
                    <li>• Group related tasks together when possible</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Team Collaboration</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Hold daily stand-up meetings to review board status</li>
                    <li>• Discuss blockers and impediments openly</li>
                    <li>• Celebrate completed tasks and milestones</li>
                    <li>• Use the board for transparent communication</li>
                    <li>• Encourage team members to update their own tasks</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Performance Monitoring</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Track cycle time from "To Do" to "Completed"</li>
                    <li>• Monitor work-in-progress limits compliance</li>
                    <li>• Identify bottlenecks and process improvements</li>
                    <li>• Measure team productivity and throughput</li>
                    <li>• Use metrics to optimize workflow efficiency</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}